import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Tag, Space, Typography, Tooltip, Button, Popconfirm, message, Input, Spin, Select, DatePicker, AutoComplete } from 'antd';
import { DeleteOutlined, SearchOutlined, LoadingOutlined, EyeOutlined, UserOutlined } from '@ant-design/icons';
import { supabase } from '../../utils/supabase';
import { ordersApi, deliveryPersonsApi, clientsApi } from '../../utils/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const OrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeliveryPersons, setLoadingDeliveryPersons] = useState(true);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [clientSearchOptions, setClientSearchOptions] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const loadOrders = async (personId = selectedDeliveryPersonId, date = selectedDate, statusFilter = selectedStatus, customerId = selectedCustomerId, tags = selectedTags) => {
    try {
      setLoading(true);
      
      // Préparer les filtres pour l'API
      const filters = {
        // Inclure les relations (customer et delivery_person) dans la réponse
        include_items: false, // On ne veut pas les items dans la liste
        order_by: 'created_at',
        ascending: false
      };

      // Filtre par livreur assigné
      if (personId) {
        filters.delivery_person_id = personId;
      }

      console.log('filters', filters);
      console.log('date', date);

      // Filtre par date de création
      if (date) {
        // Approche simple : utiliser directement le format de date
        const dateStr = date.format('YYYY-MM-DD');
        const startDate = `${dateStr} 00:00:00`;
        const endDate = `${dateStr} 23:59:59`;
        console.log('date original:', date.format('YYYY-MM-DD'));
        console.log('dateStr:', dateStr);
        console.log('startDate', startDate);
        console.log('endDate', endDate);
        filters.created_at_from = startDate;
        filters.created_at_to = endDate;
      }

      // Filtre par statut
      if (statusFilter) {
        filters.status = statusFilter;
      }

      // Filtre par client sélectionné
      if (customerId) {
        filters.customer_id = customerId;
      }

      // Filtre par tags
      if (tags && tags.length > 0) {
        filters.tags = tags.join(',');
      }

      // Appeler l'API pour récupérer les commandes avec filtres
      const response = await ordersApi.getAll(filters);
      console.log('response', response);
      if (response && response.data) {
        setOrders(response.data || []);
      } else {
        throw new Error('Erreur lors du chargement des commandes');
      }
      
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
      message.error("Erreur lors du chargement des commandes.");
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryPersons = async () => {
    try {
      setLoadingDeliveryPersons(true);
      
      // Utiliser l'API pour récupérer les livreurs actifs
      const response = await deliveryPersonsApi.getAll({ 
        is_active: true,
        order_by: 'name',
        ascending: true 
      });

      if (response && response.data) {
        setDeliveryPersons(response.data || []);
      } else {
        throw new Error('Erreur lors du chargement des livreurs');
      }
    } catch (error) {
      console.error('Error loading delivery persons:', error);
      message.error('Erreur lors du chargement des livreurs');
    } finally {
      setLoadingDeliveryPersons(false);
    }
  };

  useEffect(() => {
    loadDeliveryPersons();
  }, []);

  useEffect(() => {
    loadOrders(selectedDeliveryPersonId, selectedDate, selectedStatus, selectedCustomerId, selectedTags);
  }, [selectedDeliveryPersonId, selectedDate, selectedStatus, selectedCustomerId, selectedTags]);

  useEffect(() => {
    // Le filtrage est maintenant fait côté serveur
    setFilteredOrders(orders);
  }, [orders]);

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'pending': return 'warning';
      case 'confirmed': return 'processing';
      case 'in_delivery': return 'cyan';
      case 'delivered': return 'success';
      case 'canceled': return 'error';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const loadOrderItems = async (orderId, orderSource) => {
    if (!orderId || expandedDetails[orderId]?.items) return;

    setExpandedDetails(prev => ({ 
        ...prev, 
        [orderId]: { loading: true, items: [], error: null } 
    }));

    try {
        // Utiliser directement la source déjà connue
        const isShopifyOrder = orderSource === 'shopify';
        
        console.log(`Chargement des articles pour commande ${orderId}, source: ${orderSource}`);
        
        // Récupérer les articles de la commande avec leurs relations
        const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select(`
                id,
                product_name,
                product_price,
                quantity,
                notes,
                product_variant_id,
                external_product_id,
                product_variant: product_variants (
                    sku,
                    product: products (
                        name,
                        main_image
                    )
                )
            `)
            .eq('order_id', orderId);
            
        if (itemsError) throw itemsError;
        
        console.log("Articles bruts récupérés:", itemsData);
        
        // Pour les articles Shopify sans product_variant_id, chercher les produits par external_id
        let enrichedItems = [...itemsData];
        
        if (isShopifyOrder) {
            const itemsWithoutVariant = itemsData.filter(item => 
                !item.product_variant_id && item.external_product_id
            );
            
            console.log(`${itemsWithoutVariant.length} articles Shopify sans variant à enrichir`);
            
            // Traiter chaque article Shopify sans variant un par un
            for (const item of itemsWithoutVariant) {
                if (!item.external_product_id) continue;
                
                const shopifyProductId = `gid://shopify/Product/${item.external_product_id}`;
                console.log(`Recherche du produit avec external_id: ${shopifyProductId}`);
                
                const { data: productData, error: productError } = await supabase
                    .from('products')
                    .select('name, main_image')
                    .eq('external_id', shopifyProductId)
                    .maybeSingle();
                    
                if (!productError && productData) {
                    console.log(`Produit Shopify trouvé:`, productData);
                    
                    // Trouver l'index de l'article dans le tableau enrichi
                    const itemIndex = enrichedItems.findIndex(enrichedItem => enrichedItem.id === item.id);
                    
                    if (itemIndex !== -1) {
                        // Mettre à jour l'article avec les informations du produit
                        enrichedItems[itemIndex] = {
                            ...enrichedItems[itemIndex],
                            shopify_product: productData,
                            // Si product_name est vide, le remplacer par le nom du produit
                            product_name: enrichedItems[itemIndex].product_name || productData.name
                        };
                    }
                } else {
                    console.log(`Produit Shopify non trouvé pour external_product_id: ${item.external_product_id}`);
                }
            }
        }
        
        console.log("Articles enrichis:", enrichedItems);
        
        setExpandedDetails(prev => ({ 
            ...prev, 
            [orderId]: { loading: false, items: enrichedItems || [], error: null } 
        }));

    } catch (error) {
        console.error(`Error loading items for order ${orderId}:`, error);
        setExpandedDetails(prev => ({ 
            ...prev, 
            [orderId]: { loading: false, items: [], error: error } 
        }));
    }
  };

  const columns = [
    {
      title: 'N°',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 80,
      sorter: (a, b) => (a.order_number || 0) - (b.order_number || 0),
      render: (num) => num || 'N/A',
    },
    {
      title: 'Date Création',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      width: 180,
      render: (customer) => (
        <div>
          <div>{customer?.name || 'Client inconnu'}</div>
          <div style={{ fontSize: '0.8em', color: '#888' }}>{customer?.phone || ''}</div>
        </div>
      ),
       sorter: (a, b) => (a.customer?.name || '').localeCompare(b.customer?.name || ''),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      align: 'right',
      render: (amount) => `${Number(amount || 0).toFixed(2)} XOF`,
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    {
        title: 'Statut',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status) => (
            <Tag color={getOrderStatusColor(status)}>{status || 'N/A'}</Tag>
        ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags) => (
        <div>
          {tags && tags.length > 0 ? (
            tags.slice(0, 2).map((tag, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: '2px' }}>
                {tag}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Aucun tag</span>
          )}
          {tags && tags.length > 2 && (
            <Tag color="default">+{tags.length - 2}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Livreur',
      dataIndex: 'delivery_person',
      key: 'delivery_person',
      width: 180,
      render: (deliveryPerson) => (
          <Text type={deliveryPerson ? 'default' : 'secondary'} style={{ fontStyle: deliveryPerson ? 'normal' : 'italic' }}>
              {deliveryPerson?.name || 'Non assigné'}
          </Text>
      ),
      sorter: (a, b) => (a.delivery_person?.name || '').localeCompare(b.delivery_person?.name || ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/app/orders/${record.id}`);
          }}
          title="Voir les détails"
        >
          Détails
        </Button>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const { id: orderId, customer, delivery_address } = record;
    const details = expandedDetails[orderId];

    return (
      <div style={{ padding: '15px', backgroundColor: '#fafafa' }}>
        <Title level={5} style={{ marginBottom: '15px' }}>Détails Commande #{record.order_number || record.id}</Title>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
                <p><strong>Client:</strong> {customer?.name || 'N/A'}</p>
                <p><strong>Téléphone:</strong> {customer?.phone || 'N/A'}</p>
                <p><strong>Adresse Livraison:</strong></p>
                <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{delivery_address || 'Non fournie'}</Text>
            </div>
            
            <div>
                <p><strong>Articles commandés:</strong></p>
                {details?.loading && (
                    <div style={{ textAlign: 'center', padding: '10px' }}>
                       <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} /> Chargement...
                    </div>
                )}
                {details?.error && (
                    <Text type="danger">Erreur lors du chargement des articles.</Text>
                )}
                {!details?.loading && !details?.error && (
                    details?.items?.length > 0 ? (
                        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                            {details.items.map(item => (
                                <li key={item.id} style={{ marginBottom: '5px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {/* Image du produit (si disponible) */}
                                        {(item.product_variant?.product?.main_image || item.shopify_product?.main_image) && (
                                            <img 
                                                src={item.product_variant?.product?.main_image || item.shopify_product?.main_image} 
                                                alt="Produit"
                                                style={{ width: '40px', height: '40px', objectFit: 'cover', marginRight: '10px', borderRadius: '4px' }}
                                            />
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <Text strong>{item.quantity} x</Text> {item.product_name || item.product_variant?.product?.name || 'Article inconnu'}
                                            <Text type="secondary" style={{ marginLeft: '10px' }}>({Number(item.product_price || 0).toFixed(2)} XOF)</Text>
                                            {item.product_variant?.sku && <Text code style={{ marginLeft: '5px' }}>{item.product_variant.sku}</Text>}
                                        </div>
                                    </div>
                                    {item.notes && <Text italic style={{ display: 'block', color: '#888', marginTop: '3px' }}>Note: {item.notes}</Text>}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <>
                         {details && details.items ? 
                           <Text type="secondary">Aucun article trouvé pour cette commande.</Text> 
                           : <Text type="secondary">Cliquez pour charger les articles.</Text>} 
                         </>
                    )
                )}
            </div>
        </div>
      </div>
    );
  };

  const handleExpand = (expanded, record) => {
      if (expanded) {
          if (!expandedDetails[record.id]) {
              loadOrderItems(record.id, record.source);
          }
      }
  };

  // Fonction pour rechercher les clients
  const searchClients = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setClientSearchOptions([]);
      return;
    }

    try {
      setClientSearchLoading(true);
      
      // Rechercher les clients via l'API
      const response = await clientsApi.getAll({
        name: searchValue,
        phone: searchValue,
        limit: 10
      });
      console.log('response', response);
      if (response && response.data) {
        const options = response.data.customers.map(client => ({
          value: client.id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{client.name}</div>
                <div style={{ fontSize: '0.8em', color: '#666' }}>{client.phone}</div>
              </div>
              <UserOutlined style={{ color: '#1890ff' }} />
            </div>
          ),
          client: client
        }));
        setClientSearchOptions(options);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error);
      message.error('Erreur lors de la recherche de clients');
    } finally {
      setClientSearchLoading(false);
    }
  };

  // Fonction pour gérer la sélection d'un client
  const handleClientSelect = (value, option) => {
    if (option && option.client) {
      setSelectedCustomerId(value);
      setSelectedCustomerName(option.client.name);
      setSearchText(`${option.client.name} (${option.client.phone})`);
    }
  };

  // Fonction pour gérer le changement de texte dans l'AutoComplete
  const handleClientSearch = (value) => {
    setSearchText(value);
    
    // Si on efface le texte, réinitialiser la sélection
    if (!value) {
      setSelectedCustomerId(null);
      setSelectedCustomerName('');
      setClientSearchOptions([]);
      return;
    }

    // Débounce la recherche
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      searchClients(value);
    }, 300);
    
    setSearchTimeout(timeout);
  };

  const handleSearch = (value) => {
    // Cette fonction n'est plus utilisée car on utilise maintenant handleClientSearch
  };

  return (
    <div className="p-6">
      <Card>
        <Title level={4} style={{ marginBottom: '10px' }}>Liste des Commandes</Title>

        <Space wrap style={{ marginBottom: '20px' }}>
           <DatePicker 
              placeholder="Filtrer par date de création"
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY" 
              allowClear
            />
            <Select
              style={{ width: 200 }}
              placeholder="Filtrer par livreur"
              loading={loadingDeliveryPersons}
              value={selectedDeliveryPersonId}
              onChange={setSelectedDeliveryPersonId}
              allowClear
            >
              {deliveryPersons.map(person => (
                  <Option key={person.id} value={person.id}>
                      {person.name}
                  </Option>
              ))}
            </Select>
            <Select
              style={{ width: 150 }}
              placeholder="Filtrer par statut"
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
            >
              <Option value="new">Nouveau</Option>
              <Option value="confirmed">Confirmé</Option>
              <Option value="pending">En attente</Option>
              <Option value="in_delivery">En livraison</Option>
              <Option value="delivered">Livré</Option>
              <Option value="completed">Complété</Option>
              <Option value="canceled">Annulé</Option>
            </Select>
            <Select
              mode="tags"
              style={{ width: 200 }}
              placeholder="Filtrer par tags"
              value={selectedTags}
              onChange={(tags) => {
                // Normaliser les tags en minuscules et supprimer les doublons
                const normalizedTags = [...new Set(tags.map(tag => tag.toLowerCase().trim()))].filter(tag => tag);
                setSelectedTags(normalizedTags);
              }}
              allowClear
              tokenSeparators={[',']}
              filterOption={(input, option) => {
                // Recherche insensible à la casse dans les options
                return option.value.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {/* Options prédéfinies communes */}
              <Option value="urgent">Urgent</Option>
              <Option value="fragile">Fragile</Option>
              <Option value="gros-volume">Gros volume</Option>
              <Option value="livraison-express">Livraison express</Option>
              <Option value="paiement-cash">Paiement cash</Option>
              <Option value="client-vip">Client VIP</Option>
            </Select>
            <AutoComplete
              style={{ width: 250 }}
              placeholder="Rechercher un client..."
              value={searchText}
              options={clientSearchOptions}
              onSearch={handleClientSearch}
              onSelect={handleClientSelect}
              notFoundContent={clientSearchLoading ? <Spin size="small" /> : 'Aucun client trouvé'}
              allowClear
              onClear={() => {
                setSelectedCustomerId(null);
                setSelectedCustomerName('');
                setSearchText('');
                setClientSearchOptions([]);
              }}
            />
            <Button onClick={() => {
                setSelectedDate(null);
                setSelectedDeliveryPersonId(null);
                setSelectedStatus(null);
                setSelectedCustomerId(null);
                setSelectedCustomerName('');
                setSelectedTags([]);
                setSearchText('');
                setClientSearchOptions([]);
            }}>
                Réinitialiser Filtres
            </Button>
        </Space>

        {/* Affichage du client sélectionné */}
        {selectedCustomerId && selectedCustomerName && (
          <div style={{ 
            marginBottom: '15px', 
            padding: '10px', 
            backgroundColor: '#f0f9ff', 
            border: '1px solid #0ea5e9', 
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <UserOutlined style={{ color: '#0ea5e9', marginRight: '8px' }} />
              <span style={{ fontWeight: 'bold', color: '#0c4a6e' }}>
                Client sélectionné : {selectedCustomerName}
              </span>
            </div>
            <Button 
              type="text" 
              size="small"
              onClick={() => {
                setSelectedCustomerId(null);
                setSelectedCustomerName('');
                setSearchText('');
                setClientSearchOptions([]);
              }}
              style={{ color: '#0ea5e9' }}
            >
              ✕ Supprimer le filtre
            </Button>
          </div>
        )}

        {/* Affichage des tags sélectionnés */}
        {selectedTags && selectedTags.length > 0 && (
          <div style={{ 
            marginBottom: '15px', 
            padding: '10px', 
            backgroundColor: '#f0f2ff', 
            border: '1px solid #6366f1', 
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold', color: '#4338ca', marginRight: '8px' }}>
                Tags sélectionnés :
              </span>
              {selectedTags.map((tag, index) => (
                <Tag key={index} color="purple" style={{ margin: '2px' }}>
                  {tag}
                </Tag>
              ))}
            </div>
            <Button 
              type="text" 
              size="small"
              onClick={() => setSelectedTags([])}
              style={{ color: '#6366f1' }}
            >
              ✕ Supprimer les tags
            </Button>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={filteredOrders}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '20', '50'] }}
          scroll={{ x: 980 }}
          expandable={{
            expandedRowRender,
            rowExpandable: record => true,
            onExpand: handleExpand,
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/app/orders/${record.id}`),
            style: { cursor: 'pointer' },
            title: 'Cliquer pour voir les détails de la commande'
          })}
        />
      </Card>
    </div>
  );
};

export default OrdersList;
